import * as d3 from "d3";
import { convertToLocalTime, convertToTimeZone } from "date-fns-timezone";
import { TrendChartBinSizesEnum } from "types/enums";
import {
  FormatZonedFn,
  FlightTranslateFn,
} from "./customHooks/genericHooksTypes";

type TimeZoneType = {
  id: string;
  name: string;
  localeString: string;
  offsetInMin: number;
};

export function getDayStart(
  geDt: number,
  dayStartMsOffset: number,
  timeZoneMsOffset: number
): number {
  return new Date(geDt).setHours(0, 0, 0, 0);
}
const _60 = 60;
const _24 = 24;
const _7 = 7;

export const ONE_SECOND_OF_MS = 1000;
export const ONE_MINUTE_OF_MS = _60 * ONE_SECOND_OF_MS;
export const ONE_HOUR_OF_MS = _60 * ONE_MINUTE_OF_MS;
export const ONE_DAY_OF_MS = _24 * ONE_HOUR_OF_MS;
export const ONE_WEEK_OF_MS = _7 * ONE_DAY_OF_MS;

// Date and time format strings for use with date-fns
// Starting with v2 format and parse uses Unicode tokens.
// https://date-fns.org/docs/format -> up to date formatting documentation
// https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
export const FORMAT_READABLE_WEEK_MONTH_TIME = "EEE MMM d HH:mm";
export const FORMAT_READABLE_DATE_WITH_DAY = "EEEE MMM d, yyyy";
export const FORMAT_READABLE_DATE_WITH_SHORT_DAY = "EEE MMM d, yyyy";
export const FORMAT_READABLE_DATE = "MMM d yyyy";
export const FORMAT_HOURS = "HH";
export const FORMAT_MINUTES = "mm";
export const FORMAT_SECONDS = "ss";
export const FORMAT_TIME = "HH:mm";
export const FORMAT_DAY_TIME = "MMM d HH:mm";
export const FORMAT_DAY_TIME_WITH_SECONDS = "MMM d HH:mm:ss";
export const FORMAT_DAY_TIME_WITH_SHORT_DAY = "EEE MMM d HH:mm";
export const FORMAT_TIME_WITH_SECONDS = "HH:mm:ss";
export const FORMAT_DATE_NUMBERS = "MM/d/yyyy";
export const FORMAT_TIME_HOUR_MIN = "H:mm";
export const FORMAT_MONTH = "MMMM";
export const FULL_DATE_TIME_ZONE = "MMM d, HH:mm:ss zzz";
export const FULL_DATE_YEAR_TIME_ZONE = "MMM d y, HH:mm:ss zzz";
// use this when snapshots would include months in a different language
export const FULL_DATE_YEAR_TIME_ZONE_NO_TEXT = "M d y, HH:mm:ss zzz";
export const FULL_DATE_TIME =
  "yyyy-MM-dd HH:mm"; /* date+time localized format e.g. "2020-10-02 08:00" from https://date-fns.org/v2.16.1/docs/format */
export const FULL_DATE_TIME_SLASHES =
  "yyyy/MM/dd HH:mm"; /* slashes not dashes e.g. "2020/10/02 08:00" */
export const FULL_DATE_SLASHES = "yyyy/MM/dd";

// date range selector format
export const INPUT_FORMAT = "yyyy-MM-dd";

// Locale for use with D3
export const FORMAT_D3_DATE = "%b-%d";
export const FORMAT_D3_TIME = "%H:%M";
export const FORMAT_D3_DATE_TIME = "%b-%d %H:%M";
export const FORMAT_D3_TIME_WITH_SECONDS = "%H:%M:%S";
export const locale = d3.timeFormatLocale({
  dateTime: "%a %b %e %X %Y",
  date: "%Y-%m-%d",
  time: "%H:%M:%S",
  periods: ["am", "pm"],
  days: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  shortDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  shortMonths: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
});

// list of timezones and offsets in minutes taken from https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
// generally structured to: http://publib.boulder.ibm.com/tividd/td/TWS/SC32-1274-02/en_US/HTML/SRF_mst273.htm
export const standardTimeZones: TimeZoneType[] = [
  {
    id: "CentralAfrica",
    name: "Central African Time UTC-1",
    localeString: "Etc/GMT+1",
    offsetInMin: -60,
  },
  {
    id: "EasternBrazil",
    name: "Western Atlantic UTC-2",
    localeString: "Etc/GMT+2",
    offsetInMin: -120,
  },
  {
    id: "Argentina/brazil",
    name: "	Argentina Standard Time / Brazil Eastern Time UTC-3",
    localeString: "Etc/GMT+3",
    offsetInMin: -180,
  },
  {
    id: "NewfoundLand",
    name: "	NewfoundLand Standard UTC-3:30",
    localeString: "America/St_Johns",
    offsetInMin: -210,
  },
  {
    id: "AmericanIslandEast",
    name: "Puerto Rico / US Virgin Islands UTC-4",
    localeString: "Etc/GMT+4",
    offsetInMin: -240,
  },
  {
    id: "EasternAmerica",
    name: "Eastern Standard UTC-5",
    localeString: "Etc/GMT+5",
    offsetInMin: -300,
  },
  {
    id: "CentralAmerica",
    name: "Central Standard UTC-6",
    localeString: "Etc/GMT+6",
    offsetInMin: -360,
  },
  {
    id: "Mountain",
    name: "Pheonix / Mountain Standard UTC-7",
    localeString: "Etc/GMT+7",
    offsetInMin: -420,
  },
  {
    id: "Pacific",
    name: "Pacific Standard UTC-8",
    localeString: "Etc/GMT+8",
    offsetInMin: -480,
  },
  {
    id: "Alaska",
    name: "Alaska Standard UTC-9",
    localeString: "Etc/GMT+9",
    offsetInMin: -540,
  },
  {
    id: "Hawaii",
    name: "Hawaii Standard UTC-10",
    localeString: "Etc/GMT+10",
    offsetInMin: -600,
  },
  {
    id: "Midway",
    name: "Midway Islands UTC-11",
    localeString: "Etc/GMT+11",
    offsetInMin: -660,
  },
  {
    id: "UniversalTimeZone",
    name: "Universal Coordinated UTC+0",
    localeString: "Etc/UTC",
    offsetInMin: 0,
  },
  {
    id: "CentralEurope",
    name: "European Central UTC+1",
    localeString: "Etc/GMT-1",
    offsetInMin: 60,
  },
  {
    id: "EasternEurope",
    name: "Eastern European / Egypt Standard Time UTC+2",
    localeString: "Etc/GMT-2",
    offsetInMin: 120,
  },
  {
    id: "EastAfrica",
    name: "Eastern African Time / Middle East  UTC+3",
    localeString: "Etc/GMT-3",
    offsetInMin: 180,
  },
  {
    id: "Turkey",
    name: "Middle East UTC+3:30",
    localeString: "Asia/Tehran",
    offsetInMin: 210,
  },
  {
    id: "ArabianPeninsula",
    name: "Near East UTC+4",
    localeString: "Etc/GMT-4",
    offsetInMin: 240,
  },
  {
    id: "CentralAsia",
    name: "Pakistan Lahore UTC+5",
    localeString: "Etc/GMT-5",
    offsetInMin: 300,
  },
  {
    id: "India",
    name: "Indian Standard UTC+5:30",
    localeString: "Asia/Kolkata",
    offsetInMin: 330,
  },
  {
    id: "CentralAsiaEast",
    name: "Bangladesh Standard UTC+6",
    localeString: "Etc/GMT-6",
    offsetInMin: 360,
  },
  {
    id: "SouthEastAsia",
    name: "Vietnam Standard UTC+7",
    localeString: "Etc/GMT-7",
    offsetInMin: 420,
  },
  {
    id: "China",
    name: "China Taiwan UTC+8",
    localeString: "Etc/GMT-8",
    offsetInMin: 480,
  },
  {
    id: "Japan",
    name: "Japan Standard UTC+9",
    localeString: "Etc/GMT-9",
    offsetInMin: 540,
  },
  {
    id: "CentralAus",
    name: "Australia Central UTC+9:30",
    localeString: "Australia/Darwin",
    offsetInMin: 570,
  },
  {
    id: "EasternAustralia",
    name: "Australia Eastern UTC+10",
    localeString: "Etc/GMT-10",
    offsetInMin: 600,
  },
  {
    id: "EastRussia",
    name: "Solomon Standard UTC+11",
    localeString: "Etc/GMT-11",
    offsetInMin: 660,
  },
  {
    id: "PacificEast",
    name: "New Zealand Standard UTC+12",
    localeString: "Etc/GMT-12",
    offsetInMin: 720,
  },
];

// daylight savings changes the names and offsets of many timezones
export const daylightTimeZones: TimeZoneType[] = [
  {
    id: "CentralAfrica",
    name: "Central African Time  UTC-1",
    localeString: "Etc/GMT+1",
    offsetInMin: -60,
  },
  {
    id: "EasternBrazil",
    name: "Western Atlantic UTC-2",
    localeString: "Etc/GMT+2",
    offsetInMin: -120,
  },
  {
    id: "NewfoundLand",
    name: "	NewfoundLand Daylight UTC-2:30",
    localeString: "America/St_Johns",
    offsetInMin: -150,
  },
  {
    id: "Argentina/brazil",
    name: "	Argentina Standard Time / Brazil Eastern UTC-3",
    localeString: "Etc/GMT+3",
    offsetInMin: -180,
  },
  {
    id: "AmericanIslandEast",
    name: "Atlantic Daylight UTC-3",
    localeString: "Etc/GMT+3",
    offsetInMin: -180,
  },
  {
    id: "EasternAmerica",
    name: "Eastern Daylight UTC-4",
    localeString: "Etc/GMT+4",
    offsetInMin: -240,
  },
  {
    id: "CentralAmerica",
    name: "Central Standard UTC-5",
    localeString: "Etc/GMT+5",
    offsetInMin: -300,
  },
  {
    id: "Mountain",
    name: "Pheonix / Mountain Daylight UTC-6",
    localeString: "Etc/GMT+6",
    offsetInMin: -360,
  },
  {
    id: "Pacific",
    name: "Pacific Daylight UTC-7",
    localeString: "Etc/GMT+7",
    offsetInMin: -420,
  },
  {
    id: "Alaska",
    name: "Alaska Standard UTC-8",
    localeString: "Etc/GMT+8",
    offsetInMin: -480,
  },
  {
    id: "Hawaii",
    name: "Hawaii-Aleutian Daylight UTC-9",
    localeString: "Etc/GMT+9",
    offsetInMin: -540,
  },
  {
    id: "CookIslands",
    name: "Cook Islands Daylight UTC-10",
    localeString: "Etc/GMT+10",
    offsetInMin: -600,
  },
  {
    id: "Midway",
    name: "Midway Islands UTC-11",
    localeString: "Etc/GMT+11",
    offsetInMin: -660,
  },
  {
    id: "UniversalTimeZone",
    name: "Universal Coordinated UTC+0",
    localeString: "Etc/UTC",
    offsetInMin: 0,
  },
  {
    id: "EnglishSummer",
    name: "British Summer UTC+1",
    localeString: "Etc/GMT-1",
    offsetInMin: 60,
  },
  {
    id: "CentralEurope",
    name: "European Central Summer UTC+2",
    localeString: "Etc/GMT-2",
    offsetInMin: 120,
  },
  {
    id: "EasternEurope",
    name: "Eastern European Summer UTC+3",
    localeString: "Etc/GMT-3",
    offsetInMin: 180,
  },
  {
    // ? It gets really messy here, I need to check these again
    id: "EastAfrica",
    name: "Eastern African Time / Middle East  UTC+3",
    localeString: "Etc/GMT-3",
    offsetInMin: 180,
  },
  {
    id: "Turkey",
    name: "Middle East UTC+4:30",
    localeString: "Asia/Tehran",
    offsetInMin: 270,
  },
  {
    id: "ArabianPeninsula",
    name: "Near East UTC+4",
    localeString: "Etc/GMT-4",
    offsetInMin: 240,
  },
  {
    id: "CentralAsia",
    name: "Pakistan Lahore UTC+5",
    localeString: "Etc/GMT-5",
    offsetInMin: 300,
  },
  {
    id: "India",
    name: "Indian Standard UTC+5:30",
    localeString: "Asia/Kolkata",
    offsetInMin: 330,
  },
  {
    id: "CentralAsiaEast",
    name: "Bangladesh Standard UTC+6",
    localeString: "Etc/GMT-6",
    offsetInMin: 360,
  },
  {
    id: "SouthEastAsia",
    name: "Vietnam Standard UTC+7",
    localeString: "Etc/GMT-7",
    offsetInMin: 420,
  },
  {
    id: "China",
    name: "China Taiwan UTC+8",
    localeString: "Etc/GMT-8",
    offsetInMin: 480,
  },
  {
    id: "Japan",
    name: "Japan Standard UTC+9",
    localeString: "Etc/GMT-9",
    offsetInMin: 540,
  },
  {
    id: "CentralAus",
    name: "Australia Central UTC+9:30",
    localeString: "Australia/Darwin",
    offsetInMin: 570,
  },
  {
    id: "EasternAustralia",
    name: "Australia Eastern UTC+10",
    localeString: "Etc/GMT-10",
    offsetInMin: 600,
  },
  {
    id: "EastRussia",
    name: "Solomon Standard UTC+11",
    localeString: "Etc/GMT-11",
    offsetInMin: 660,
  },
  {
    id: "PacificEast",
    name: "New Zealand Standard UTC+12",
    localeString: "Etc/GMT-12",
    offsetInMin: 720,
  },
];

/**
 * Common timezone strings
 */
export const AMERICA_NEW_YORK = "America/New_York";
export const AMERICA_CHICAGO = "America/Chicago";
export const UNITED_KINGDOM = "Etc/UTC";
export const US_PACIFIC = "America/Los_Angeles";

/**
 * Used to calculate the timezone offset difference of daylight savings and none daylight savings.
 * The variable date will give us the desired year.
 * offset in minutes
 *
 * @param [date] input if standard offset wants to be calculated off another unknown year
 */
const getStandardOffset = (date: Date = new Date()): number => {
  // we decide whether we ant to use current date, or given date

  // comparing offsets for june 1st, and january 1st should account for an offset (exluding southern hemisphere)
  const january = new Date(date.getFullYear(), 0, 1);
  const july = new Date(date.getFullYear(), 6, 1);

  return Math.max(january.getTimezoneOffset(), july.getTimezoneOffset());
};

export const getIsDaylightSavings = (date: Date = new Date()): boolean => {
  // check the current date/time if there is one given, use that

  return date.getTimezoneOffset() < getStandardOffset(date);
};

/**
 * function determins the string for our current timezone
 *
 * @param date Date to determine the timeZone
 */
export const determineCurrentTimeZone = (
  date: Date = new Date()
): TimeZoneType => {
  const timeZoneFilterFunction = timeZone =>
    timeZone.offsetInMin === -date.getTimezoneOffset(); // there is a negative on timezoneOffset, because time is counted in an inverted fashion

  const selectedTimezones = getIsDaylightSavings(date)
    ? daylightTimeZones.filter(timeZoneFilterFunction)
    : standardTimeZones.filter(timeZoneFilterFunction);

  return selectedTimezones[0];
};

const MAX_DURATION_MS = ONE_DAY_OF_MS * 999;
const truncateTime = ({
  durationMs,
  showCountdown = false,
  isShortForm = false,
}: {
  durationMs: number;
  showCountdown?: boolean;
  isShortForm?: boolean;
}): { displayedTime: number | string; displayedUnit: string } => {
  // return null-like if > 999 days (data not ready)
  if (
    durationMs > MAX_DURATION_MS ||
    (showCountdown && durationMs < -MAX_DURATION_MS)
  ) {
    return { displayedTime: "--", displayedUnit: "" };
  }

  // for countdown, allow negatives
  const minimum = showCountdown ? -Infinity : 0;

  // start with minutes for countdown timer

  // Floor the time, but don't display negative durations (except in countdown mode).
  // Negative durations can happen when clocks are not in sync.
  const minutes = Math.max(minimum, Math.floor(durationMs / ONE_MINUTE_OF_MS));
  // if minutes is 0, show "<1" instead of 0
  let displayedTime = minutes === 0 ? "<1" : minutes;
  let displayedUnit = isShortForm ? "m" : "min.";

  if (typeof displayedTime === "string") {
    // Don't do anything else if we're already displaying "<1"
  } else if (displayedTime > _60 || (showCountdown && displayedTime < -_60)) {
    // if > 1 hour, format as hrs
    const hours = Math.floor(durationMs / ONE_HOUR_OF_MS);
    displayedTime = hours;
    displayedUnit = isShortForm ? "h" : displayedTime < 2 ? "hr." : "hrs.";
    // if > 1 day, format as days
    if (displayedTime > _24 || (showCountdown && displayedTime < -_24)) {
      displayedTime = Math.floor(durationMs / ONE_DAY_OF_MS);
      displayedUnit = isShortForm ? "d" : displayedTime < 2 ? "day" : "days";
    }
  }

  return { displayedTime, displayedUnit };
};

export const convertSecondsToFormattedMinutes = targetRate => {
  const minutes = Math.floor(targetRate / _60);
  const seconds = targetRate - _60 * minutes;
  return minutes > 0 ? `${minutes}m${seconds}s` : `${seconds}s`;
};

export default truncateTime;

export const getFormattedTimes = ({
  time,
  formatZoned,
}: {
  time: number;
  formatZoned: FormatZonedFn;
}) => {
  const hoursMinutes = formatZoned(time, FORMAT_TIME);
  const hour = formatZoned(time, FORMAT_HOURS);
  const minute = formatZoned(time, "mm");
  const day = formatZoned(time, "d");
  const month = formatZoned(time, "MMM");
  const shortWeekday = formatZoned(time, "EEE");
  return {
    hoursMinutes,
    hour,
    minute,
    day,
    month,
    shortWeekday,
  };
};

export const getPnlFormattedHoursMinutes = (timeMs?: number | null) => {
  const totalMinutes = (timeMs || 0) / ONE_MINUTE_OF_MS;

  const minutesRemainder = totalMinutes % _60;
  const hoursWithoutRemainder = (totalMinutes - minutesRemainder) / _60;

  let hoursDisplay = hoursWithoutRemainder.toFixed(0);
  let minutesDisplay = minutesRemainder.toFixed(0);

  // if minutes rounds up to 60,
  // set minutes to 0
  // add 1 to hours
  if (minutesDisplay === "60") {
    minutesDisplay = "0";
    hoursDisplay = String(Number(hoursWithoutRemainder + 1));
  }

  return { hours: hoursDisplay, minutes: minutesDisplay };
};

export const getMidnightTomorrowMs = (now, timeZoneMsOffset) =>
  getDayStart(now + ONE_DAY_OF_MS, 0, timeZoneMsOffset);
export const getMidnightTodayMs = (now, timeZoneMsOffset) =>
  getDayStart(now, 0, timeZoneMsOffset);

export const addOneHourToStringTime = timeString => {
  return `${parseInt(timeString, 10) + ONE_HOUR_OF_MS}`;
};

export const getFullDayRangeTitle = (startTimeMs, endTimeMs, formatZoned) =>
  `${formatZoned(startTimeMs, FORMAT_DAY_TIME)}-${formatZoned(
    endTimeMs,
    FORMAT_DAY_TIME
  )}`;

export function formatDateTimeInput(time: number, formatZoned: FormatZonedFn) {
  return `${formatZoned(time, INPUT_FORMAT)}T${formatZoned(time, FORMAT_TIME)}`;
}

export const TRENDS_RANGE_MAX_RECOMMENDED_MACHINES_TO_LOAD_TIMELINE = {
  // these are just guesses
  day: 40,
  week: 20,
  month: 10,
  ninetyDays: 5,
};

export const loop = ({ cb, interval, ref }) => {
  return window.setTimeout(() => {
    cb();
    ref.current = loop({ cb, interval, ref });
  }, interval);
};

/**
 * This function is a higher level function which determines whether we need to convert a date to a different timezone
 * if conversion is required, we call convertTimeZoneOfDate to run the conversion
 *
 * @param shouldUseCustomTimeZone Should time zone conversion take place
 * @param date date object or unix time stamp to convert
 * @param timeZoneLocaleString the time zone locale string of the desired time zone
 * @param toLocal set true if wish to convert a zoned date to local time
 */
export const generateTimeZoneAwareDate = (
  shouldUseCustomTimeZone: boolean,
  date: Date | number,
  timeZoneLocaleString: string,
  toLocal = false
): Date =>
  shouldUseCustomTimeZone
    ? toLocal
      ? convertToLocalTime(date, { timeZone: timeZoneLocaleString })
      : convertTimeZoneOfDate(date, timeZoneLocaleString)
    : new Date(date);

/**
 * this function converts a given date object to a desired time zone for printing purposes
 *
 * @param date Date or unix timestamp that will be converted
 * @param timeZoneLocaleString the time zone locale string of the desired time zone
 */
export const convertTimeZoneOfDate = (
  date: Date | number,
  timeZoneLocaleString: string
) => {
  return convertToTimeZone(date, { timeZone: timeZoneLocaleString });
};

/** minimum selectable date using the date picker calendar */
export const MIN_DATE = new Date(2019, 0, 2);

export const TRENDS_BIN_SIZE_VALUES_MS_MAP = {
  [TrendChartBinSizesEnum.hours]: ONE_HOUR_OF_MS,
  [TrendChartBinSizesEnum.days]: ONE_DAY_OF_MS,
  [TrendChartBinSizesEnum.weeks]: 7 * ONE_DAY_OF_MS,
};

/**
 * Get date object of desired day of the week
 * https://stackoverflow.com/questions/4156434/javascript-get-the-first-day-of-the-week-from-current-date
 *
 * @param {number} targetWeekDayIdx
 * @param {Date} [currentDate] optional param for testing
 * @return {*}  {Date}
 */
export const getDateOfCurrentWeekDayNum = ({
  targetWeekDayIdx,
  currentDate,
}: {
  targetWeekDayIdx: number;
  currentDate?: Date;
}): Date => {
  const dateObj = currentDate || new Date(Date.now());
  const diff = dateObj.getDate() - (dateObj.getDay() - targetWeekDayIdx);
  return new Date(dateObj.setDate(diff));
};

// copied from https://stackoverflow.com/questions/11887934/how-to-check-if-dst-daylight-saving-time-is-in-effect-and-if-so-the-offset
function stdTimezoneOffset(date: Date) {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);
  return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
}
/** is a date in Daylight Savings Time? pass nothing to check the current time */
export function isDstObserved(dateToCheck?: Date) {
  const date = dateToCheck || new Date();
  return date.getTimezoneOffset() < stdTimezoneOffset(date);
}

/**
 * Given milliseconds, returns formatted times
 */
export function getFormattedDurationTimes(
  milliseconds: number,
  formatZoned: FormatZonedFn,
  translate: FlightTranslateFn
): {
  days: number;
  hours: number;
  minutes: number;
  minutesPadded: string; // 00, 01, etc
  displayedTime1: number; // first number of the status e.g. "1" in "1h2m"
  displayedUnit1: string; // first unit of the status e.g. "h" in "1h2m"
  displayedTime2: number; // second number of the status e.g. "2" in "1h2m"
  displayedUnit2: string; // second unit of the status e.g. the "m" in "1h2m"
  isNegative: boolean;
  isLessThanOneMinute: boolean;
} {
  // for positive numbers, we round the bigger units down, for negative numbers, we round up
  const isNegative = milliseconds < 0;
  const mathFn = isNegative ? "ceil" : "floor";

  const days = Math[mathFn](milliseconds / ONE_DAY_OF_MS);
  const hours = Math[mathFn]((milliseconds % ONE_DAY_OF_MS) / ONE_HOUR_OF_MS);
  const minutes = Math[mathFn](
    (milliseconds % ONE_HOUR_OF_MS) / ONE_MINUTE_OF_MS
  );
  const minutesPadded = formatZoned(minutes * ONE_MINUTE_OF_MS, FORMAT_MINUTES);

  // formatted time + units:
  //  e.g. 1h 00m, 50m, 10m, 9m59s, 59s
  let displayedTime1, displayedUnit1, displayedTime2, displayedUnit2;
  let isLessThanOneMinute = false;
  if (days) {
    displayedTime1 = days;
    displayedUnit1 = translate("days_short_hand");
    displayedTime2 = hours;
    displayedUnit2 = translate("hours_short_hand");
  } else if (hours) {
    displayedTime1 = hours;
    displayedUnit1 = translate("hours_short_hand");
    displayedTime2 = minutes;
    displayedUnit2 = translate("minutes_short_hand");
  } else {
    // if (minutes)
    displayedTime1 = minutes;
    displayedUnit1 = translate("minutes_short_hand");
    // only show "<1" if we're not negative
    // otherwise show "0" and then "-1, -2" etc
    if (minutes < 1 && !isNegative) {
      isLessThanOneMinute = true;
    }
  }

  return {
    days,
    hours,
    minutesPadded,
    minutes,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    isNegative,
    isLessThanOneMinute,
  };
}

export function formatHoursMinutes(durationMs: number) {
  const duration = durationMs / 1000;
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  return hours < 1 ? `${minutes}m` : `${hours}h ${minutes}m`;
}

// ! this function is currently unused, but I tested & it gives correct hour offset numbers, whereas getTimezoneOffset from date-fns seems to give incorrect results e.g. pass in "Europe/London" while in GMT-5 tz -> expect 5, but date-fns gives 0
/** given a timeZone key like "Europe/London", convert to hours of offset from current timezone */
export function getTimezoneOffsetHours(timeZoneKey: string): number {
  const now = new Date();

  // Get the time in the local timezone
  const localTime = now.getTime();

  // Get the time in the target timezone
  const targetTime = new Date(
    now.toLocaleString("en-US", { timeZone: timeZoneKey })
  ).getTime();

  // Calculate the difference in hours
  const offsetHours = (targetTime - localTime) / (1000 * 60 * 60);

  // Round to the nearest quarter hour to handle fractional offsets
  return Math.round(offsetHours * 4) / 4;
}
